import * as React from 'react'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  XIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Localization from '../../localization/LocalizationManager'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import { notifyOverlayOpen } from '../../utils/overlayNotifier'
import { Button } from '../Button/Button'
import InfinityCalendar from '../CommonDatePicker/InfinityCalendar/InfinityCalendar'
import MenuCloser from '../MenuCloser'
import {
  MAX_MONTHS_BACK,
  MAX_MONTHS_FORWARD,
} from './CommonDateFilter.constants'
import CommonDateFilterStore from './CommonDateFilter.store'
import {
  ALL_DATE_FILTER_MODES,
  CommonDateFilterMode,
  getCommonDateFilterModeDisplayName,
} from './CommonDateFilterMode'
import MonthsPicker from './MonthsPicker'
import YearPicker from './YearPicker'

import './CommonDateFilter.scss'

interface IProps {
  store: CommonDateFilterStore
  projectDateStore?: ProjectDateStore
}

const ICON_SIZE = 18

const TOTAL_MONTHS = MAX_MONTHS_BACK + MAX_MONTHS_FORWARD + 1

function generateMonths(projectDateStore: ProjectDateStore): Date[] {
  const months: Date[] = []
  const today = new Date()
  const startMonth = projectDateStore.addMonths(today, -MAX_MONTHS_BACK)

  for (let i = 0; i < TOTAL_MONTHS; i++) {
    months.push(projectDateStore.addMonths(startMonth, i))
  }

  return months
}

@inject('projectDateStore')
@observer
export default class CommonDateFilter extends React.Component<IProps> {
  private months: Date[]
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)
    this.months = generateMonths(props.projectDateStore)
  }

  public componentDidUpdate() {
    const isShown = this.props.store.isCalendarShown
    if (isShown && !this.closeOverlay) {
      this.closeOverlay = notifyOverlayOpen()
    } else if (!isShown && this.closeOverlay) {
      this.closeOverlay()
      this.closeOverlay = null
    }
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
  }

  public render() {
    const { store } = this.props
    const { isCalendarShown } = store

    return (
      <MenuCloser
        closeMenu={store.cancelPendingSelection}
        isOpen={isCalendarShown}
      >
        <div className="common-date-filter row y-center">
          {this.renderTodayButton()}
          {this.renderDateRangeButton()}
          {this.renderNavigationArrows()}
          {this.renderModeSelector()}
          {this.renderCalendarModal()}
        </div>
      </MenuCloser>
    )
  }

  private renderTodayButton(): JSX.Element | null {
    const { store } = this.props

    if (store.isProjectMode) {
      return null
    }

    return (
      <Button variant="outline" onClick={store.setDateToToday}>
        {Localization.translator.today}
      </Button>
    )
  }

  private renderDateRangeButton(): JSX.Element {
    const { store, projectDateStore } = this.props
    const {
      isCalendarShown,
      datesButtonCaption,
      isCurrentActive,
      isYearPickerMode,
      isProjectMode,
    } = store

    return (
      <div className="date-range-selector">
        <div
          className={classList({
            'date-range-btn': true,
            active: isCalendarShown,
            'current-lookahead': isCurrentActive,
            disabled: isProjectMode,
          })}
          onClick={isProjectMode ? undefined : store.toggleCalendar}
        >
          <span className="date-range-text">{datesButtonCaption}</span>
          {!isProjectMode &&
            (isCalendarShown ? (
              <ChevronUpIcon className="chevron-icon" size={ICON_SIZE} />
            ) : (
              <ChevronDownIcon className="chevron-icon" size={ICON_SIZE} />
            ))}
        </div>
        {isYearPickerMode && isCalendarShown && (
          <YearPicker
            selectedYear={store.selectedYear}
            onYearSelect={store.handleYearSelect}
            projectDateStore={projectDateStore}
          />
        )}
      </div>
    )
  }

  private renderNavigationArrows(): JSX.Element {
    const { store } = this.props
    const { canMoveBack, canMoveForward } = store

    return (
      <div className="navigation-arrows">
        <span
          className={classList({
            'nav-arrow': true,
            disabled: !canMoveBack,
          })}
          onClick={canMoveBack ? store.moveDatesBack : undefined}
        >
          <ChevronLeftIcon size={ICON_SIZE} />
        </span>
        <span
          className={classList({
            'nav-arrow': true,
            disabled: !canMoveForward,
          })}
          onClick={canMoveForward ? store.moveDatesForward : undefined}
        >
          <ChevronRightIcon size={ICON_SIZE} />
        </span>
      </div>
    )
  }

  private renderModeSelector(): JSX.Element {
    const { store } = this.props
    const { dateFilterMode, shouldShowModeOptions } = store

    return (
      <div className="mode-selector">
        <MenuCloser closeMenu={store.hideModeOptions}>
          <div
            className={classList({
              'mode-btn': true,
              active: shouldShowModeOptions,
            })}
            onClick={store.toggleModeOptions}
          >
            <span>{getCommonDateFilterModeDisplayName(dateFilterMode)}</span>
            {shouldShowModeOptions ? (
              <ChevronUpIcon size={ICON_SIZE} />
            ) : (
              <ChevronDownIcon size={ICON_SIZE} />
            )}
          </div>
          {shouldShowModeOptions && (
            <div className="mode-options absolute">
              {ALL_DATE_FILTER_MODES.map(mode => (
                <div
                  key={mode}
                  className={classList({
                    'mode-option': true,
                    active: dateFilterMode === mode,
                  })}
                  onClick={() => store.setDateFilterMode(mode)}
                >
                  <span>{getCommonDateFilterModeDisplayName(mode)}</span>
                  {dateFilterMode === mode && (
                    <CheckIcon size={16} className="tw:shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}
        </MenuCloser>
      </div>
    )
  }

  private renderCalendarModal(): JSX.Element | null {
    const { store, projectDateStore } = this.props
    const {
      isCalendarShown,
      isYearPickerMode,
      filters,
      endDate,
      pendingStartDate,
      pendingEndDate,
      dateFilterMode,
    } = store

    // Don't show calendar modal in year picker mode (year picker is shown separately)
    if (!isCalendarShown || isYearPickerMode) {
      return null
    }

    const displayStartDate = pendingStartDate ?? filters.startDate
    const displayEndDate = pendingEndDate ?? endDate

    return (
      <div className="infinity-calendar-modal-container shown">
        <div className="dimmer shown" onClick={store.cancelPendingSelection} />
        <div className="infinity-calendar-modal shown">
          <div className="calendar-modal-header">
            <XIcon
              size={16}
              className="close-btn"
              onClick={store.cancelPendingSelection}
            />
            <div className="row x-center header-title">
              {this.getHeaderTitle()}
            </div>
          </div>
          <div className="calendar-content">
            {this.shouldShowMonthsPicker() ? (
              <MonthsPicker
                key={`${displayStartDate?.getTime()}-${displayEndDate?.getTime()}`}
                currentMode={dateFilterMode}
                startDate={displayStartDate}
                endDate={displayEndDate}
                onMonthSelect={this.handleMonthSelect}
                projectDateStore={projectDateStore}
              />
            ) : (
              <InfinityCalendar
                key={`${displayStartDate?.getTime()}-${displayEndDate?.getTime()}`}
                months={this.months}
                dateClickHandler={this.handleDateClick}
                startDate={displayStartDate}
                endDate={displayEndDate}
                projectDateStore={projectDateStore}
                monthHeight={350}
              />
            )}
          </div>
          <div className="calendar-modal-footer">
            <Button
              variant="outline"
              size="lg"
              onClick={store.cancelPendingSelection}
            >
              {Localization.translator.cancel}
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={store.applyPendingSelection}
            >
              {Localization.translator.apply}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  private handleDateClick = (_event: Event, date: Date) => {
    this.props.store.handleDateSelect(date)
  }

  private handleMonthSelect = (date: Date) => {
    this.props.store.handleMonthSelect(date)
  }

  private shouldShowMonthsPicker(): boolean {
    const { dateFilterMode } = this.props.store
    return (
      dateFilterMode === CommonDateFilterMode.MONTH ||
      dateFilterMode === CommonDateFilterMode.THREE_MONTHS
    )
  }

  private getHeaderTitle(): string {
    const { dateFilterMode } = this.props.store
    if (dateFilterMode === CommonDateFilterMode.MONTH) {
      return Localization.translator.selectMonth
    }
    if (dateFilterMode === CommonDateFilterMode.THREE_MONTHS) {
      return Localization.translator.selectDateRange
    }
    return Localization.translator.selectDate
  }
}
